import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
// import useWebSocket from 'react-use-websocket';
import Menu from '../../components/Menu/Menu';
import Ticker from './Ticker/Ticker';
import Book from './Book/Book';
import Wallet from './Wallet/Wallet';
import CandleChart from './CandleChart';
import NewOrderButton from '../../components/NewOrder/NewOrderButton';
import NewOrderModal from '../../components/NewOrder/NewOrderModal';
import SelectSymbol from '../../components/SelectSymbol/SelectSymbol';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import { getEmptyMessage } from '../../utils/MessageUtils';
import { DEFAULT_SYMBOL } from '../../utils/SymbolUtils';
import { getStoredValue, setStoredValue } from '../../utils/StorageUtils';
import SetHomePageButton from '../../components/SetHomePageButton/SetHomePageButton';
import { HOME_DASHBOARD } from '../../utils/HomePageUtils';
import AppWebSocket from '../../components/AppWebSocket/AppWebSocket';
import OnboardingChecklist from './OnboardingChecklist';
import DashboardSummary from './DashboardSummary';
import Watchlist from './Watchlist';
import SetupAssistant from '../../components/SetupAssistant/SetupAssistant';
import { getSettings, getMarketTicker24h } from '../../services/SettingsService';
import { getAiProviders } from '../../services/AiProvidersService';
import OpenPositions from './OpenPositions';
import AllocationCard from './AllocationCard';
import ScanRecommendationsPanel from './ScanRecommendationsPanel';
import OperationalHealthPanel from './OperationalHealthPanel';
import IdleStatusCard from './IdleStatusCard';
import BusinessHealthBanner from '../../components/BusinessHealthBanner/BusinessHealthBanner';
import { getCurrentMarket, subscribeToCurrentMarket, MARKET_FUTURES } from '../../utils/MarketStorage';
import { getCurrentEnvironment, subscribeToCurrentEnvironment } from '../../utils/CurrentEnvironmentStorage';
import { getFuturesAccount } from '../../services/FuturesService';
import FuturesAccountCard from '../../components/FuturesAccountCard/FuturesAccountCard';
import { Link } from 'react-router-dom';

function Dashboard() {
    const history = useHistory();

    const [market, setMarket] = useState(getCurrentMarket());
    const [environment, setEnvironment] = useState(getCurrentEnvironment());
    const [futAccount, setFutAccount] = useState({ positions: [], balance: null });
    const isFutures = market === MARKET_FUTURES;

    const [tickerState, setTickerState] = useState({});

    const [bookState, setBookState] = useState({});
    const [highestGap, setHighestGap] = useState({});

    const [balanceState, setBalanceState] = useState({});

    // eslint-disable-next-line no-unused-vars
    const [wallet, setWallet] = useState({});

    const [chartSymbol, setChartSymbol] = useState(loadChartSymbol());
    const [aiSettings, setAiSettings] = useState({});
    // Incrementar abre o Assistente de configuração manualmente (botão no card de Primeiros Passos).
    const [assistantOpenTrigger, setAssistantOpenTrigger] = useState(0);

    const [notification, setNotification] = useState(getEmptyMessage());
    const [scanRefreshTick, setScanRefreshTick] = useState(0);

    useEffect(() => {
        Promise.all([getSettings(), getAiProviders().catch(() => [])])
            .then(([s, providers]) => {
                // Usa aiProvider legado ou, se vazio, o primary da nova tabela
                const activeProvider = s.aiProvider
                    || (providers.find(p => p.role === 'primary' && p.isActive) || providers.find(p => p.isActive) || {}).provider
                    || null;
                setAiSettings({ aiProvider: activeProvider, aiMonthlyTokenLimit: s.aiMonthlyTokenLimit, aiAutoSwitch: s.aiAutoSwitch });
            })
            .catch(() => {});
    }, []);

    useEffect(() => subscribeToCurrentMarket(m => setMarket(m)), []);
    useEffect(() => subscribeToCurrentEnvironment(env => setEnvironment(env)), []);

    // Mercado 24h + Book via REST (poll 8s). O feed WS !ticker@arr da Binance morreu; o ticker
    // por-símbolo só cobre os ativos com hard stop. Este snapshot alimenta Ticker/Book/OpenPositions/
    // Watchlist. O WS (book dos símbolos com automação :TICKER) ainda sobrepõe em cima destes.
    useEffect(() => {
        if (isFutures) return; // futuros usa a conta de margem, não o cluster spot
        let alive = true;
        function pull() {
            getMarketTicker24h()
                .then(snap => {
                    if (!alive || !snap) return;
                    if (snap.ticker) setTickerState(prev => ({ ...prev, ...snap.ticker }));
                    if (snap.book) setBookState(prev => ({ ...prev, ...snap.book }));
                })
                .catch(() => { /* silencioso: é enriquecimento de exibição, não bloqueia a tela */ });
        }
        pull();
        const id = setInterval(pull, 8000);
        return () => { alive = false; clearInterval(id); };
    }, [isFutures]);

    // Em FUTUROS o Dashboard troca o cluster spot pela conta de margem + posições (read-only).
    useEffect(() => {
        if (!isFutures) return;
        getFuturesAccount(environment)
            .then(setFutAccount)
            .catch(() => setFutAccount({ positions: [], balance: null }));
    }, [isFutures, environment]);

    function loadChartSymbol() {
        return getStoredValue('dashboardChartSymbol', DEFAULT_SYMBOL);
    }

    function saveChartSymbol(value) {
        setStoredValue('dashboardChartSymbol', value);
    }

    function onWalletUpdate(walletObj) {
        setWallet(walletObj);
    }

    function onOrderSubmit(order) {
        history.push('/orders/' + order.symbol);
    }

    /*
    const { lastJsonMessage: message } = useWebSocket(process.env.REACT_APP_WS_URL, {
        onOpen: () => console.log(`Connected to App WS Server`),
        onMessage: () => {
            if (message) {
                if (message.ticker) setTickerState(message.ticker);
                if (message.book) {
                    message.book.forEach(b => {
                        if (b.highestGap)
                            setHighestGap(b.highestGap);
                        else
                            bookState[b.symbol] = b;
                    });
                    setBookState(bookState);
                }
                if (message.balance) setBalanceState(message.balance);
            }
        },
        queryParams: { "token": getToken() }, // parametros no get
        onError: (event) => setNotification(processWebSocketError(event)),
        shouldReconnect: (closeEvent) => true,
        reconnectInterval: 3000
        // reconnectAttempts: 20 (default)
    })
    */

    function onAppMessage(message) {
        if (message.ticker) setTickerState(message.ticker);
        if (message.book) {
            message.book.forEach(b => {
                if (b.highestGap)
                    setHighestGap(b.highestGap);
                else
                    bookState[b.symbol] = b;
            });
            setBookState(bookState);
        }
        if (message.balance) setBalanceState(message.balance);
    } 
    
    function onAppNotification(notification) {
        setNotification(notification);
        if (notification && notification.text && notification.text.includes('Scanner IA'))
            setScanRefreshTick(t => t + 1);
    }

    function onChangeSymbol(event) {
        saveChartSymbol(event.target.value);
        setChartSymbol(event.target.value);
    }

    const [orderPrefill, setOrderPrefill] = useState(null);

    function onAiOrderRequest(symbol, signal) {
        const side = signal && signal.signal === 'SELL' ? 'SELL' : 'BUY';
        setOrderPrefill({
            symbol,
            side,
            type: 'MARKET',
            aiSignal: signal || null
        });
        // Bootstrap programmatic show
        try {
            const el = document.getElementById('modalOrder');
            if (el && window.bootstrap) {
                window.bootstrap.Modal.getOrCreateInstance(el).show();
            }
        } catch (_) { /* graceful no-op */ }
    }

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <div className='btn-toolbar mb-md-0'>
                            <SetHomePageButton value={HOME_DASHBOARD} />
                            <h1 className="h4">
                                Dashboard
                                <span className={`badge ms-2 ${isFutures ? 'bg-warning text-dark' : 'bg-secondary'}`} style={{ fontSize: '0.5em', verticalAlign: 'middle' }}>
                                    {isFutures ? 'Futuros' : 'Spot'}
                                </span>
                            </h1>
                        </div>
                    </div>
                    {!isFutures && (
                        <div className='btn-toolbar mb-md-0'>
                            <div className="d-inline-flex align-items-center">
                                <SelectSymbol symbol={chartSymbol} hideSelectOption={true} onChange={onChangeSymbol} />
                            </div>
                            <div className="ms-2 ms-lg-3">
                                <NewOrderButton />
                            </div>
                        </div>
                    )}
                </div>
                <BusinessHealthBanner />
                <OperationalHealthPanel refreshTick={scanRefreshTick} />
                {!isFutures && <IdleStatusCard refreshTick={scanRefreshTick} />}
                {/* Resumo (estratégias/ordens/P&L) e Pulso do Mercado são spot/global, não escopados
                    por mercado → escondidos em Futuros pra não exibir números de spot na lente errada. */}
                {!isFutures && (
                    <>
                        <DashboardSummary aiProvider={aiSettings.aiProvider} aiMonthlyTokenLimit={aiSettings.aiMonthlyTokenLimit} />
                        <ScanRecommendationsPanel
                            refreshTick={scanRefreshTick}
                            aiAutoSwitch={aiSettings.aiAutoSwitch}
                            aiProvider={aiSettings.aiProvider}
                        />
                    </>
                )}
                <OnboardingChecklist onOpenAssistant={() => setAssistantOpenTrigger(t => t + 1)} />
                {isFutures ? (
                    <>
                        <FuturesAccountCard account={futAccount} />
                        <div className='text-center mb-4'>
                            <Link to='/futures' className='btn btn-outline-warning btn-sm'>Ir para a mesa de Futuros →</Link>
                        </div>
                    </>
                ) : (
                    <>
                        <AllocationCard />
                        <OpenPositions tickerData={tickerState} />
                        <Watchlist tickerData={tickerState} onAiOrder={onAiOrderRequest} />
                        <CandleChart symbol={chartSymbol} />
                        <Ticker data={tickerState} />
                        <div className="row">
                            <Book data={bookState} highestGap={highestGap} />
                            <Wallet data={balanceState} onUpdate={onWalletUpdate} />
                        </div>
                    </>
                )}
                <Footer />
            </main>
            <NewOrderModal onSubmit={onOrderSubmit} prefill={orderPrefill} />
            <AppWebSocket onMessage={onAppMessage} onNotification={onAppNotification} />
            <SetupAssistant openTrigger={assistantOpenTrigger} />
            <Toast type={notification.type} text={notification.text} listenMessages={false} />
        </React.Fragment>
    );
}

export default Dashboard;